import { useState } from "react";
import { Phone, Mail, Globe, Building2, Send, MapPin } from "lucide-react";
import { company, directors } from "../mock";
import PageHero from "../components/PageHero";
import { useToast } from "../hooks/use-toast";
import { createEnquiry } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the required fields", description: "Name, email and message are required." });
      return;
    }
    setSubmitting(true);
    try {
      await createEnquiry(form);
      toast({ title: "Enquiry sent", description: "Thank you — we'll be in touch shortly." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again, or call us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHero
        eyebrow="Get In Touch"
        title="Contact"
        subtitle="Tell us about your project and our team will get back to you with expert advice and a competitive quotation."
      />

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-[1fr,1.3fr]">
          {/* Contact info */}
          <div>
            <h2 className="font-head text-2xl font-700 text-[#0f2a5a]">Contact Details</h2>
            <div className="ld-accent-bar mt-4" />
            <p className="mt-5 text-sm leading-relaxed text-gray-600">
              We provide nationwide supply and installation of window controls, automation and
              electrical solutions. Reach out to either of our directors directly.
            </p>

            <div className="mt-8 space-y-5">
              {directors.map((d) => (
                <div key={d.email} className="rounded-xl border border-gray-100 bg-gray-50 p-5">
                  <p className="font-head font-700 text-[#0f2a5a]">{d.name} <span className="text-xs font-semibold uppercase tracking-wider text-[#1d4ed8]">&middot; {d.role}</span></p>
                  <a href={`tel:${d.phone.replace(/\s/g, "")}`} className="mt-3 flex items-center gap-3 text-sm text-gray-700 hover:text-[#1d4ed8]">
                    <Phone size={16} className="text-[#1d4ed8]" /> {d.phone}
                  </a>
                  <a href={`mailto:${d.email}`} className="mt-2 flex items-center gap-3 text-sm text-gray-700 hover:text-[#1d4ed8]">
                    <Mail size={16} className="text-[#1d4ed8]" /> {d.email}
                  </a>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3 text-sm text-gray-700">
              <p className="flex items-center gap-3"><Mail size={16} className="text-[#1d4ed8]" /> <a href="mailto:enquiries@ld-controls.com" className="hover:text-[#1d4ed8]">enquiries@ld-controls.com</a></p>
              <p className="flex items-center gap-3"><Globe size={16} className="text-[#1d4ed8]" /> {company.website}</p>
              <p className="flex items-center gap-3"><MapPin size={16} className="text-[#1d4ed8]" /> Mainland UK, including the Isle of Wight</p>
              <p className="flex items-center gap-3"><Building2 size={16} className="text-[#1d4ed8]" /> {company.name} &middot; Company No. {company.companyNumber}</p>
            </div>
          </div>

          {/* Form */}
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-7 md:p-9">
            <h2 className="font-head text-2xl font-700 text-[#0f2a5a]">Request a Quote</h2>
            <div className="ld-accent-bar mt-4" />
            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Name *</label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" className="bg-white" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Phone</label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Your phone" className="bg-white" />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Email *</label>
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" className="bg-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Enquiry Type</label>
                <Select value={form.subject} onValueChange={(v) => update("subject", v)}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual Winders">Teleflex Manual Winders</SelectItem>
                    <SelectItem value="Electric Motors">Electric Window Motors</SelectItem>
                    <SelectItem value="Automation">Natural Ventilation / Automation</SelectItem>
                    <SelectItem value="Electrical">Electrical Solutions</SelectItem>
                    <SelectItem value="Installation">Installation Service</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Message *</label>
                <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your project..." rows={5} className="bg-white" />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary inline-flex w-full items-center justify-center gap-2 rounded-md px-6 py-3.5 text-sm font-semibold disabled:opacity-60">
                {submitting ? "Sending..." : <>Send Enquiry <Send size={16} /></>}
              </button>
              <p className="text-center text-xs text-gray-400">
                We aim to respond to all enquiries within one working day.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
